import type { WorkerBridge } from './WorkerBridge.js'
import type { CommerceHttpAdapter } from '../client/CommerceHttpAdapter.js'

/**
 * Stratégies de cache disponibles pour les requêtes HTTP.
 */
export type CacheStrategyName =
  | 'network-only'       // Jamais de cache (mutations)
  | 'network-first'      // Réseau, fallback cache si erreur réseau
  | 'cache-first'        // Cache, revalidation réseau en background si périmé
  | 'stale-while-revalidate' // Retourne le cache immédiatement + revalide en arrière-plan

/** TTL par stratégie, en millisecondes */
export const DEFAULT_TTL: Record<Exclude<CacheStrategyName, 'network-only'>, number> = {
  'network-first': 30_000,        // 30 secondes (session, panier)
  'cache-first': 5 * 60_000,     // 5 minutes (recherche)
  'stale-while-revalidate': 60 * 60_000 // 1 heure (catalogue)
}

export class CacheStrategy {
  constructor(
    private readonly bridge: WorkerBridge,
    private readonly http: CommerceHttpAdapter
  ) {}

  /**
   * Exécute la requête selon la stratégie choisie.
   *
   * @param key        Clé de cache (généralement le path + params)
   * @param fetcher    Fonction qui effectue la requête HTTP
   * @param strategy   Stratégie à appliquer
   * @param ttlMs      TTL du cache (override la valeur par défaut)
   */
  async execute<T>(
    key: string,
    fetcher: () => Promise<T>,
    strategy: CacheStrategyName,
    ttlMs?: number
  ): Promise<T> {
    switch (strategy) {
      case 'network-only':
        return fetcher()

      case 'network-first':
        return this.networkFirst<T>(key, fetcher, ttlMs ?? DEFAULT_TTL['network-first'])

      case 'cache-first':
        return this.cacheFirst<T>(key, fetcher, ttlMs ?? DEFAULT_TTL['cache-first'])

      case 'stale-while-revalidate':
        return this.staleWhileRevalidate<T>(key, fetcher, ttlMs ?? DEFAULT_TTL['stale-while-revalidate'])
    }
  }

  private async networkFirst<T>(key: string, fetcher: () => Promise<T>, ttlMs: number): Promise<T> {
    try {
      const data = await fetcher()
      await this.bridge.set(key, data, ttlMs)
      return data
    } catch (err) {
      // Erreur réseau uniquement (TypeError) : on tente le cache
      if (err instanceof TypeError) {
        const cached = await this.bridge.get<T>(key)
        if (cached !== null) return cached
      }
      throw err
    }
  }

  private async cacheFirst<T>(key: string, fetcher: () => Promise<T>, ttlMs: number): Promise<T> {
    const cached = await this.bridge.get<T>(key)
    if (cached !== null) return cached

    const data = await fetcher()
    await this.bridge.set(key, data, ttlMs)
    return data
  }

  private async staleWhileRevalidate<T>(key: string, fetcher: () => Promise<T>, ttlMs: number): Promise<T> {
    const cached = await this.bridge.get<T>(key)

    // Revalidation en arrière-plan (ne bloque pas le résultat)
    const revalidate = fetcher()
      .then((data) => this.bridge.set(key, data, ttlMs))
      .catch(() => { /* Échec silencieux : on garde le cache */ })

    if (cached !== null) {
      // On lance la revalidation sans attendre
      void revalidate
      return cached
    }

    // Pas de cache : on attend le réseau
    const data = await fetcher()
    await this.bridge.set(key, data, ttlMs)
    return data
  }
}
